namespace $ {

	export type $shm_projects_data_version = {
		release_notes?: $shm_projects_release_notes
		id: string
		run_id?: string
		version: string
		date: string
		requirements?: string
		changes: string | readonly ( string | $shm_projects_data_change )[]
		stage: 'Пререлиз' | 'Релиз'
		prerelease_kind?: 'fast' | 'full'
		prerelease_full_reason?: string
		artifacts?: readonly $shm_projects_data_artifact[]
		tests?: readonly $shm_projects_data_test[]
		benchmarks?: readonly $shm_projects_data_benchmark[]
		available?: boolean
		stages?: readonly $shm_projects_data_stage[]
	}

	export type $shm_projects_data_stage = {
		id: string
		title: string
		started_at: number
		completed_at?: number
		elapsed_ms: number
		status: 'running' | 'done' | 'failed'
	}

	export type $shm_projects_data_change = {
		title: string
		description?: string
		screenshots?: readonly string[]
	}

	export type $shm_projects_data_artifact = {
		kind: 'screenshot' | 'video' | 'report'
		title: string
		path: string
	}

	export type $shm_projects_data_test = {
		scenario: string
		title: string
		run: string
		status: string
		manifest: string
	}

	export type $shm_projects_data_benchmark = $shm_projects_data_test & {
		dimension?: {
			title: string
			item_title?: string
		}
		values: readonly {
			step: string
			title: string
			value: number
			unit: string
			better: 'lower' | 'higher'
			note?: string
		}[]
	}

	export type $shm_projects_data_tests = {
		scenarios: readonly string[]
		artifacts?: readonly {
			scenario: string
			include?: readonly string[]
		}[]
	}

	export type $shm_projects_data_product = {
		description: string
		problem: string
		audience: string
		value_proposition: string
		scenarios: string
		success_criteria: string
		stage: 'idea' | 'prototype' | 'mvp' | 'growing' | 'support' | 'archived'
	}

	export type $shm_projects_data_development = {
		id: string
		source_project_id: string
		source_project_title: string
		status: 'queued' | 'running' | 'waiting' | 'done' | 'failed' | 'stopped'
		busy: boolean
		phase: string
		updated_at: number
		changed_modules: readonly string[]
		affected_modules: readonly string[]
		reason: string
	}

	export type $shm_projects_data_record = {
		id: string
		title: string
		fqn?: string
		builder?: 'mam' | 'none'
		type?: 'complex' | 'client' | 'server'
		directory: string
		version_file?: string
		directory_pending?: boolean
		archived?: boolean
		parent_id?: string
		readme: string
		agents?: string
		repositories?: readonly string[]
		dependencies?: readonly { kind: 'mam', repository: string, title: string }[]
		remote_repository?: $shm_projects_github_repository
		base_branch?: string
		preview: string
		preview_default?: boolean
		delivery?: {
			type: 'hosted'
			project: string
			application: string
		}
		product?: $shm_projects_data_product
		deployment?: {
			driver: 'docker'
			context: string
			image: string
			target: string
			targets?: {
				prerelease: string
				release: string
			}
			server: string
			prerelease?: {
				guide?: string
				fast?: {
					strategy: 'files'
					artifacts: 'hot'
					paths: readonly string[]
					ignore_paths?: readonly string[]
					allow_paths?: readonly string[]
					exclude_paths?: readonly string[]
					exclude_suffixes?: readonly string[]
					components: readonly {
						id: string
						mounts: readonly { source: string, target: string, read_only?: boolean }[]
						backup_volume?: string
					}[]
				}
			}
			bundle?: {
				runtime: 'static' | 'node'
				modules: readonly string[]
				exclude?: readonly string[]
				dockerfile?: string
				dockerignore?: string
				files: readonly {
					source: string
					target: string
				}[]
				preview?: {
					environment_from?: {
						compose_project: string
						service: string
						keys: readonly string[]
					}
					volumes?: readonly {
						name: string
						target: string
					}[]
				}
			}
			components?: readonly $shm_projects_release_component[]
		}
		version: string
		version_date: string
		channels?: {
			prerelease?: string
			release?: string
		}
		tests?: $shm_projects_data_tests
		benchmarks?: { commands: readonly string[] }
		history: readonly $shm_projects_data_version[]
		developments?: readonly $shm_projects_data_development[]
		modules?: readonly ( $shm_projects_graph_module & { readme?: string } )[]
	}

	export class $shm_projects_data extends $mol_object2 {
		static request( selector: string, value: string ) {
			const url = new URL( this.$.$shm_projects_config.projects_api_url() )
			url.searchParams.set( selector, value )
			const token = this.$.$shm_projects_config.agent_token()
			return this.$.$mol_fetch.json(
				url.toString(),
				{
					cache: 'no-store',
					headers: token ? { 'X-Hitalama-Agent-Token': token } : {},
				},
			) as unknown
		}

		@ $mol_mem
		static summaries( next?: readonly $shm_projects_api_project_summary[] ) {
			const api = this.$.$shm_projects_config.projects_api_url()
			const identity = this.$.$shm_projects_config.agent_token()
			const key = this.$.$shm_projects_data_cache.key( api, identity )
			// Cookie-only authentication has no stable client identity. Do not reuse
			// another account's persistent snapshot on that authenticated origin.
			const persistent = !this.$.$shm_projects_config.site_api_url() || Boolean( identity )
			return this.summaries_scoped( [ key, persistent ], next )
		}

		@ $mol_mem_key
		static summaries_scoped( scope: readonly [ string, boolean ], next?: readonly $shm_projects_api_project_summary[] ): readonly $shm_projects_api_project_summary[] {
			const [ key, persistent ] = scope
			const cache = this.$.$shm_projects_data_cache
			if( next !== undefined ) {
				const summaries = cache.normalize( next )
				return persistent ? cache.write( key, summaries ) : summaries
			}
			const stored = persistent ? cache.read( key ) : null
			return this.$.$mol_error_fence(
				()=> {
					const fresh = cache.normalize( this.request( 'project_summaries', '1' ) )
					if( stored && this.$.$mol_compare_deep( stored, fresh ) ) return stored
					return persistent ? cache.write( key, fresh ) : fresh
				},
				error => this.$.$mol_fail_hidden( error ),
				promise => stored ?? this.$.$mol_fail_hidden( promise ),
			)
		}

		static summary( id: string ) {
			return this.summaries().find( project => project.id === id ) ?? null
		}

		static summary_sync( project: $shm_projects_api_project_summary_source ) {
			const summaries = this.summaries()
			const index = summaries.findIndex( current => current.id === project.id )
			const previous = index < 0 ? null : summaries[ index ]
			const summary = this.$.$shm_projects_api_project_summary({
				...project,
				...( !project.channels && previous?.channels ? { channels: previous.channels } : {} ),
			})
			const next = [ ... summaries ]
			if( index < 0 ) next.push( summary )
			else next[ index ] = summary
			this.summaries( next )
			return summary
		}

		static records_sync( records: readonly $shm_projects_data_record[] ) {
			const ids = new Set( records.map( project => project.id ) )
			this.summaries( this.summaries().filter( project => ids.has( project.id ) ) )
			for( const project of records ) this.summary_sync( project )
			return this.records( records )
		}

		static detail_sync( project: $shm_projects_data_record ) {
			this.summary_sync( project )
			return this.detail( project.id, project )
		}

		@ $mol_mem
		static developments( next?: readonly $shm_projects_api_development_summary[] ) {
			if( next ) return next
			const response = this.request( 'developments', '1' )
			if( !Array.isArray( response ) ) throw new Error( 'Project developments response must be an array' )
			return response as readonly $shm_projects_api_development_summary[]
		}

		@ $mol_mem_key
		static detail( id: string, next?: $shm_projects_data_record | null ) {
			if( next !== undefined ) return next
			if( !id ) return null
			const response = this.request( 'project_detail', id )
			if( !response || typeof response !== 'object' || Array.isArray( response ) ) {
				throw new Error( 'Project detail response must be an object' )
			}
			const project = response as $shm_projects_data_record
			if( project.id !== id || !project.title || !Array.isArray( project.history ) ) {
				throw new Error( `Invalid project detail: ${ id }` )
			}
			return project
		}

		@ $mol_mem
		static records( next?: readonly $shm_projects_data_record[] ): readonly $shm_projects_data_record[] {
			if( next ) return next
			const response = this.request( 'projects', '1' )
			if( !Array.isArray( response ) ) {
				const message = response && typeof response === 'object' && 'error' in response
					? String( response.error )
					: 'Projects API response must be an array'
				throw new Error( message )
			}
			const records = response as readonly $shm_projects_data_record[]
			const ids = new Set< string >()

			for( const record of records ) {
				if( !record.id || !record.title ) throw new Error( 'Project id and title are required' )
				if( ids.has( record.id ) ) throw new Error( `Duplicate project id: ${ record.id }` )
				ids.add( record.id )
			}

			return records
		}

		static record( id: string ) {
			return this.records().find( record => record.id === id ) ?? null
		}

		static delivery( record: $shm_projects_data_record ) {
			return $shm_projects_delivery.project( this.records(), record )
		}

		static preview( record: $shm_projects_data_record ) {
			return $shm_projects_delivery.preview( this.records(), record )
		}

		static state_key( id: string, field: string ) {
			return `shm_projects:${ id }:${ field }`
		}

		static session_ids( id: string, next?: readonly string[] ) {
			return this.$.$mol_state_local.value< readonly string[] >( this.state_key( id, 'opencode_sessions' ), next ) ?? []
		}

		static session_current( id: string, next?: string ) {
			return this.$.$mol_state_local.value< string >( this.state_key( id, 'opencode_session_current' ), next ) ?? ''
		}

		static version( id: string, next?: string ) {
			if( next !== undefined ) this.$.$mol_state_local.value< string >( this.state_key( id, 'version' ), next )
			return this.record( id )?.version ?? next ?? ''
		}

		static version_date( id: string, next?: string ) {
			if( next !== undefined ) this.$.$mol_state_local.value< string >( this.state_key( id, 'version_date' ), next )
			const date = this.record( id )?.version_date ?? next ?? ''
			return date === '-' ? '' : date
		}

		static version_changes( id: string, next?: string ) {
			return this.$.$mol_state_local.value< string >( this.state_key( id, 'version_changes' ), next ) ?? ''
		}

		static requirements( id: string, next?: string ) {
			return this.$.$mol_state_local.value< string >( this.state_key( id, 'requirements' ), next ) ?? ''
		}

		static requirements_version( id: string, next?: string ) {
			return this.$.$mol_state_local.value< string >( this.state_key( id, 'requirements_version' ), next ) ?? ''
		}

		static version_history( id: string, next?: readonly $shm_projects_data_version[] ) {
			const key = this.state_key( id, 'version_history' )
			if( next !== undefined ) return this.$.$mol_state_local.value< readonly $shm_projects_data_version[] >( key, next ) ?? next
			const stored = this.$.$mol_state_local.value< readonly $shm_projects_data_version[] >( key ) ?? []
			const seeded = this.record( id )?.history ?? []
			if( !stored.length ) return seeded
			const versions = new Set( seeded.map( record => `${ record.stage }:${ record.version }` ) )
			return [ ...seeded, ...stored.filter( record => !versions.has( `${ record.stage }:${ record.version }` ) ) ]
		}

	}

}
